import * as helios from "@hyperionbt/helios"
import common from "./common.hl"
//wraps helios.Program
export default class Program {
    #program

    constructor(parameters = {}) {
        // load all the dependencies
        const allDeps = [common]

        const depSrcs = new Map()

        function addDepSrcs(dep) {
            depSrcs.set(dep.name, dep.src)

            for (let d of dep.dependencies) {
                addDepSrcs(d)
            }
        }

        for (let d of allDeps) {
            addDepSrcs(d)
        }

        this.#program = helios.Program.new(`minting mp_collection

import {
  TOKEN_NAME_PROTOCOL_PARAMS,
  TOKEN_NAME_AUTH,
  DatumProtocolParams,
  DatumArtwork
} from common

// Parameters
const MASTER_MPH: MintingPolicyHash = MintingPolicyHash::new(#)
const ARTWORK_SCRIPT_ADDRESS: Address = Address::new(
  Credential::new_validator(ValidatorHash::new(#)), Option[StakingCredential]::None
)
const OWNER_ADDRESS: Address = Address::new(
  Credential::new_pubkey(PubKeyHash::new(#)), Option[StakingCredential]::None
)
const COLLECTION_ID: ByteArray = #

enum Redeemer {
  CreateArtwork
  MintArtwork
}

func main(redeemer: Redeemer, ctx: ScriptContext) -> Bool {
  protocol_params: DatumProtocolParams = ctx.tx.ref_inputs
    .find((input: TxInput) -> {
      input.output.value.get_safe(AssetClass::new(MASTER_MPH, TOKEN_NAME_PROTOCOL_PARAMS)) == 1
    })
    .output.datum.switch {
      i: Inline => DatumProtocolParams::from_data(i.data),
      else => error("Invalid protocol params UTxO: missing inline datum")
    };

  signed_by_owner: Bool = OWNER_ADDRESS.credential.switch {
    pk: PubKey => ctx.tx.is_signed_by(pk.hash),
    else => error("Invalid owner address")
  };

  redeemer.switch {
    CreateArtwork => {
      // We delegate checks off-chain as we require both signatures anyway.
      ctx.tx.is_signed_by(protocol_params.mod_pkh) &&
      signed_by_owner
    },
    MintArtwork => {
      // We enforce only one collection to avoid a fee UTxO satisfying multiple collections.
      // It's not like we can mint many cross collections with multiple minting policy in a tx.
      // Minting multiple editions from the same collection is quite good already.
      assert(ctx.tx.minted.get_assets().to_map().length == 1, "Expect exactly one collection");

      own_mph: MintingPolicyHash = ctx.get_current_minting_policy_hash();
      own_minted: Map[ByteArray]Int = ctx.tx.minted.get_policy(own_mph);

      // Artwork UTxOs must always be consumed and updated correctly.
      datums: []DatumArtwork = ctx.tx.inputs // outputs are cheaper but less intuitive.
        .filter((input: TxInput) -> {
          input.output.address == ARTWORK_SCRIPT_ADDRESS &&
          input.output.value.get_safe(AssetClass::new(own_mph, TOKEN_NAME_AUTH)) == 1
        })
        .map((input: TxInput) -> {
          input.output.datum.switch {
            i: Inline => DatumArtwork::from_data(i.data),
            else => error("Invalid artwork output: missing inline datum")
          }
        });

      // Goal: enforce protocol fees from the owner
      if (signed_by_owner) {
        protocol_fee: Int = own_minted.fold(
          (fee: Int, token_name: ByteArray, token_minted: Int) -> {
            assert(token_name != TOKEN_NAME_AUTH, "Do not touch auth tokens");
            assert(datums.any((datum: DatumArtwork) -> {
              datum.owner_address == OWNER_ADDRESS &&
              datum.collection_id == COLLECTION_ID &&
              datum.token_name == token_name
            }), "Must consume the Artwork UTxO always");

            fee + if (token_minted <= 0) {
              0
            } else {
              protocol_params.kreator_mint_fee * token_minted
            }
          }, 0);

        protocol_fee == 0 || ctx.tx.outputs.any((output: TxOutput) -> {
          output.address == protocol_params.treasury_address &&
          output.value.get_lovelace() >= protocol_fee
        })
      }
      // Goal: enforce mint fees for the owner
      // The protocol fee is negotiated off-chain with the moderator signature.
      else {
        // To avoid third-party metadata updates
        assert(ctx.tx.is_signed_by(protocol_params.mod_pkh), "Missing moderator signature");

        mint_fee: Int = own_minted.fold(
          (fee: Int, token_name: ByteArray, token_minted: Int) -> {
            // Enforce that the datum exists always.
            datum: DatumArtwork = datums.find((datum: DatumArtwork) -> {
              datum.owner_address == OWNER_ADDRESS &&
              datum.collection_id == COLLECTION_ID &&
              datum.token_name == token_name
            });

            fee + if (token_minted <= 0) {
              0
            } else {
              datum.mint_fee * token_minted
            }
          }, 0);

        mint_fee == 0 || ctx.tx.outputs.any((output: TxOutput) -> {
          output.address == OWNER_ADDRESS &&
          output.value.get_lovelace() >= mint_fee
        })
      }
    }
  }
}
`, Array.from(depSrcs.values()))

        if (Object.keys(parameters).length > 0) {
            this.#program.parameters = parameters
        }
    }

    get name() {
        return this.#program.name
    }

    get paramTypes() {
        return this.#program.paramTypes
    }

    get parameters() {
        return this.#program.parameters
    }

    get types() {
        return this.#program.types
    }

    set parameters(params) {
        this.#program.parameters = params
    }

    compile(optimize = false) {
        return this.#program.compile(optimize)
    }

    evalParam(paramName) {
        return this.#program.evalParam(paramName)
    }
}
